import type { DeliverySlot } from "@utils/store/cart/useOrder";

type Slot = DeliverySlot["slots"][number];

/** 本地時間日期字串 YYYY-MM-DD */
export function todayStr(now: Date = new Date()): string {
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
}

/** 當日已過時段：deliveryDate 為今天且 slot end_time 已過 → true（本地時間） */
export function isSlotPast(deliveryDate: string, endTime: string, now: Date = new Date()): boolean {
  if (deliveryDate !== todayStr(now)) return false;
  const [h, m] = endTime.split(":").map(Number);
  return new Date(now.getFullYear(), now.getMonth(), now.getDate(), h || 0, m || 0).getTime() <= now.getTime();
}

/** 容量判定：未帶 available_capacity 視為不限量；帶了則需 > 0 */
export function slotHasCapacity(s: Slot): boolean {
  return s.available_capacity !== undefined ? s.available_capacity > 0 : true;
}

/** 單一時段可預約：有容量且未過時 */
export function slotAvailable(date: string, s: Slot, now: Date = new Date()): boolean {
  return slotHasCapacity(s) && !isSlotPast(date, s.end_time, now);
}

/**
 * 可預約日期清單：API 有回、非整日公休、且至少一個可用時段。
 * 入口閘門與時段選擇器共用同一套判斷，確保「閘門說可預約」與「選擇器有可選日」一致。
 */
export function getReservableDays(slots: DeliverySlot[] | null | undefined, now: Date = new Date()): string[] {
  return (slots || [])
    .filter((d) => !d.is_closed_all_day && (d.slots || []).some((s) => slotAvailable(d.delivery_date, s, now)))
    .map((d) => d.delivery_date);
}

/** 是否還有任何可預約時段（公休但仍可預約其他日期時 → true） */
export function hasReservableSlots(slots: DeliverySlot[] | null | undefined, now: Date = new Date()): boolean {
  return getReservableDays(slots, now).length > 0;
}

/**
 * 目前選取的「日期＋時段」是否仍然成立：該日仍可預約、slot_id 仍在今次清單中、且未過時／未額滿。
 *
 * 只檢查 `!!slotId` 是不夠的：slotId 會從草稿（syncDraft）或 sessionStorage 殘留下來，
 * 而那個 id 可能已不在今次 deliverySlots 裡（換瓦斯行、換日、時段被移除）。
 * 這時畫面上沒有任何時段是選取狀態，「下一步」卻是可按的——使用者沒選時段就能進到下一步，
 * 還會帶著一個失效的 slot_id 去送單。
 */
export function isSlotSelected(
  slots: DeliverySlot[] | null | undefined,
  date: string | null,
  slotId: number | null,
  now: Date = new Date(),
): boolean {
  if (!date || slotId == null) return false;
  const day = (slots || []).find((d) => d.delivery_date === date);
  if (!day || day.is_closed_all_day) return false;
  const s = (day.slots || []).find((x) => x.slot_id === slotId);
  return !!s && slotAvailable(date, s, now);
}
